import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../styles/RecentDocuments.css';
import { API_BASE_URL } from '../utils/apiConfig';
import DocumentModal from './DocumentModal';
import PaymentModal from '../components/UnifiedPaymentModal';
import StatusLight from './StatusLight';
import DocumentCreationModal from '../components/DocumentCreationModal';
import usePermissions from '../utils/usePermissions';
import PaginationControls from '../components/PaginationControls';
import usePagination from '../utils/usePagination';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';

const InvoiceListPage = ({ currentLogo }) => {
  const { can, getAction } = usePermissions('invoices');
  const { showInlineNotice } = useInlineNotice();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentDocumentId, setPaymentDocumentId] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [documentToCopy, setDocumentToCopy] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ status: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [documentToCancel, setDocumentToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [sortConfig, setSortConfig] = useState({
    column: 'createdAt',
    direction: 'desc'
  });
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);
  const formatCurrency = (value) => (
    formatCurrencyBySystem(Number(value || 0), getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    })
  );

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setCurrentUserId(userData.id || userData._id || null);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('editDraft'),
    canCopy: can('copy'),
    canCancel: can('cancel'),
    canDelete: can('delete'),
    canPayments: can('payments')
  }), [can]);

  const requiresOwnerForEdit = useMemo(() => (
    (getAction('editDraft')?.scope || '').includes('own')
  ), [getAction]);

  const requiresOwnerForCancel = useMemo(() => (
    (getAction('cancel')?.scope || '').includes('own')
  ), [getAction]);

  const fetchInvoices = async (searchTerm = '') => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/documents`, {
        params: searchTerm ? { search: searchTerm } : undefined
      });
      // Filter only invoices
      const invoiceDocs = (res.data || []).filter(doc => doc.type === 'Invoice');
      setInvoices(invoiceDocs);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(`Failed to load invoices: ${err.response?.data?.error || err.message}`);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    fetchInvoices(activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchInvoices(activeSearchTerm);
  };

  const fetchDocumentDetails = async (documentId) => {
    setDocumentLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/documents/${documentId}`);
      setSelectedDocument(res.data);
      setModalOpen(true);
    } catch (err) {
      console.error('Error fetching document details:', err);
      setError(`Failed to load document details: ${err.response?.data?.error || err.message}`);
    } finally {
      setDocumentLoading(false);
    }
  };

  const fetchDocumentForEdit = async (documentId) => {
    setDocumentLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/documents/${documentId}`);
      setDocumentToEdit(res.data);
      setShowEditModal(true);
    } catch (err) {
      console.error('Error fetching document details:', err);
      setError(`Failed to load document details: ${err.response?.data?.error || err.message}`);
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleEditDocument = () => {
    if (selectedRows.length !== 1) return;
    const docToEdit = invoices.find(doc => doc._id === selectedRows[0]);
    if (!permissions.canEdit) return;
    const canEdit = docToEdit && (docToEdit.status || 'Draft') === 'Draft' && (
      !requiresOwnerForEdit || docToEdit.createdById === currentUserId
    );
    if (canEdit) {
      fetchDocumentForEdit(docToEdit._id);
    }
  };

  const handleCopyDocument = () => {
    if (selectedRows.length !== 1) return;
    if (!permissions.canCopy) return;
    const docToCopy = invoices.find(doc => doc._id === selectedRows[0]);
    setDocumentToCopy(docToCopy);
    setShowCopyConfirm(true);
  };

  const confirmCopyDocument = async () => {
    try {
      setCopyLoading(true);
      setShowCopyConfirm(false);
      
      const response = await axios.post(`${API_BASE_URL}/documents/${selectedRows[0]}/copy`);
      
      if (response.data.success) {
        const docNumber = documentToCopy?.documentNumber || 'invoice';
        showInlineNotice(`Invoice ${docNumber} copied successfully.`, {
          scope: 'invoices'
        });
        await fetchInvoices(activeSearchTerm);
        setSelectedRows([]);
      }
    } catch (err) {
      console.error('Error copying document:', err);
      setError(`Failed to copy invoice: ${err.response?.data?.error || err.message}`);
      showInlineNotice('Failed to copy invoice.', {
        scope: 'invoices',
        type: 'error'
      });
    } finally {
      setCopyLoading(false);
      setDocumentToCopy(null);
    }
  };

  const cancelCopyDocument = () => {
    setShowCopyConfirm(false);
    setDocumentToCopy(null);
  };

  const handleStatusClick = (documentId, status) => {
    if (!permissions.canPayments) return;
    if (status === 'Confirmed' || status === 'In Progress') {
      setPaymentDocumentId(documentId);
      setPaymentModalOpen(true);
    }
  };

  const handlePaymentSaved = async () => {
    setPaymentModalOpen(false);
    setPaymentDocumentId(null);
    await fetchInvoices(activeSearchTerm);
    showInlineNotice('Payment saved successfully.', {
      scope: 'invoices'
    });
  };

  const handleViewDocument = () => {
    if (selectedRows.length === 1) {
      fetchDocumentDetails(selectedRows[0]);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDocument(null);
  };

  const handleCheckboxChange = (documentId) => {
    setSelectedRows(prev =>
      prev.includes(documentId)
        ? []
        : [documentId]
    );
  };

  const getFilteredAndSortedDocuments = () => {
    let docs = invoices.filter(doc => {
      if (selectedFilters.status && doc.status !== selectedFilters.status) {
        return false;
      }
      return true;
    });

    docs = docs.sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.column) {
        case 'documentNumber':
          aVal = a.documentNumber || '';
          bVal = b.documentNumber || '';
          break;
        case 'customer':
          aVal = a.customer || '';
          bVal = b.customer || '';
          break;
        case 'createdBy':
          aVal = a.createdBy || '';
          bVal = b.createdBy || '';
          break;
        case 'createdAt':
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'total':
          aVal = parseFloat(a.total) || 0;
          bVal = parseFloat(b.total) || 0;
          break;
        case 'status':
          aVal = a.status || 'Draft';
          bVal = b.status || 'Draft';
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return docs;
  };

  const handleSort = (column) => {
    if (sortConfig.column === column) {
      setSortConfig({
        column,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      setSortConfig({
        column,
        direction: 'asc'
      });
    }
  };

  const handleFilterChange = (status) => {
    setSelectedFilters({ status });
  };

  const handleClearFilters = () => {
    setSelectedFilters({ status: null });
  };

  const handleDocumentCreated = async (newDocument, meta = {}) => {
    if (newDocument?.documentNumber) {
      const mode = meta.mode === 'update' ? 'updated' : 'created';
      showInlineNotice(`Invoice ${newDocument.documentNumber} ${mode} successfully.`, {
        scope: 'invoices'
      });
    }
    await fetchInvoices(activeSearchTerm);
    setSelectedRows([]);
  };

  const handleDeleteDocument = (document) => {
    if (!permissions.canDelete) return;
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDocument = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/documents/${documentToDelete._id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const docNumber = documentToDelete?.documentNumber || 'invoice';
      showInlineNotice(`Invoice ${docNumber} deleted successfully.`, {
        scope: 'invoices'
      });
      
      await fetchInvoices(activeSearchTerm);
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.error || 'Failed to delete document');
      setShowDeleteConfirm(false);
      showInlineNotice('Failed to delete invoice.', {
        scope: 'invoices',
        type: 'error'
      });
    }
  };

  const handleCancelDocument = () => {
    if (selectedRows.length !== 1) return;
    if (!permissions.canCancel) return;
    const docToCancel = invoices.find(doc => doc._id === selectedRows[0]);
    const canCancelDoc = docToCancel && (
      requiresOwnerForCancel
        ? docToCancel.status === 'Draft' && docToCancel.createdById === currentUserId
        : ['Draft', 'Confirmed'].includes(docToCancel.status || 'Draft')
    );
    if (canCancelDoc) {
      setDocumentToCancel(docToCancel);
      setCancelReason('');
      setShowCancelConfirm(true);
    }
  };

  const confirmCancelDocument = async () => {
    try {
      if (!cancelReason.trim()) {
        setError('Cancellation reason is required.');
        return;
      }
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/documents/${documentToCancel._id}/cancel`,
        { cancellationReason: cancelReason.trim() },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const docNumber = documentToCancel?.documentNumber || 'invoice';
      showInlineNotice(`Invoice ${docNumber} cancelled successfully.`, {
        scope: 'invoices'
      });
      
      await fetchInvoices(activeSearchTerm);
      setShowCancelConfirm(false);
      setDocumentToCancel(null);
      setCancelReason('');
      setSelectedRows([]);
    } catch (err) {
      console.error('Error cancelling document:', err);
      setError(err.response?.data?.error || 'Failed to cancel document');
      setShowCancelConfirm(false);
      showInlineNotice('Failed to cancel invoice.', {
        scope: 'invoices',
        type: 'error'
      });
    }
  };

  const filteredDocuments = getFilteredAndSortedDocuments();
  const {
    page,
    setPage,
    totalPages,
    pageItems: pagedDocuments,
    totalItems,
    pageSize
  } = usePagination(filteredDocuments, 10);

  useEffect(() => {
    setSelectedRows([]);
  }, [page, activeSearchTerm]);

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Invoices <span className="text-muted">({filteredDocuments.length})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="invoices" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={handleReload}
              disabled={loading}
              className="recent-invoices-btn recent-invoices-btn-reload"
            >
              {loading ? (
                <>
                  <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                  Reload
                </>
              )}
            </button>
            {permissions.canEdit && (
              <button 
                onClick={handleEditDocument}
                disabled={
                  selectedRows.length !== 1 ||
                  (() => {
                    const doc = invoices.find(d => d._id === selectedRows[0]);
                    if (!doc || (doc.status || 'Draft') !== 'Draft') return true;
                    if (requiresOwnerForEdit && doc.createdById !== currentUserId) return true;
                    return false;
                  })()
                }
                className="recent-invoices-btn recent-invoices-btn-copy"
                title="Edit selected draft invoice"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                Edit
              </button>
            )}
            <button 
              onClick={handleViewDocument}
              disabled={selectedRows.length === 0 || documentLoading}
              className="recent-invoices-btn recent-invoices-btn-view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              View
            </button>
            {permissions.canCopy && (
              <button 
                onClick={handleCopyDocument}
                disabled={selectedRows.length === 0 || copyLoading}
                className="recent-invoices-btn recent-invoices-btn-copy"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                Copy
              </button>
            )}
            <button 
              onClick={() => setShowFilterModal(true)}
              className="recent-invoices-btn recent-invoices-btn-filter"
              title="Filter invoices by status"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              Filter
            </button>
            {permissions.canCancel && (
              <button 
                onClick={handleCancelDocument}
                disabled={
                  selectedRows.length !== 1 ||
                  (() => {
                    const doc = invoices.find(d => d._id === selectedRows[0]);
                    if (!doc) return true;
                    if (requiresOwnerForCancel) {
                      return doc.status !== 'Draft' || doc.createdById !== currentUserId;
                    }
                    return !['Draft', 'Confirmed'].includes(doc.status || 'Draft');
                  })()
                }
                className="recent-invoices-btn recent-invoices-btn-cancel"
                title="Cancel selected invoice"
                style={{ backgroundColor: '#f97316', color: 'white' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Cancel
              </button>
            )}
            {permissions.canDelete && (
              <button 
                onClick={() => {
                  if (selectedRows.length === 1) {
                    handleDeleteDocument(invoices.find(doc => doc._id === selectedRows[0]));
                  }
                }}
                disabled={selectedRows.length === 0}
                className="recent-invoices-btn recent-invoices-btn-delete"
                title="Delete selected invoice"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                Delete
              </button>
            )}
            </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ListSearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                    placeholder="Search by Document #, Customer, Created By"
                  disabled={loading}
                  isSearching={loading && Boolean(activeSearchTerm)}
                />
                {permissions.canCreate && (
                  <button
                      onClick={() => setShowCreationModal(true)}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add
                  </button>
                )}
              </div>
          </div>
        </div>
        {invoices.length === 0 ? (
          <div className="recent-invoices-empty">
            {activeSearchTerm ? 'No invoices match your search.' : 'No invoices found.'}
          </div>
        ) : (
          <table className="recent-invoices-table">
            <thead>
              <tr>
                <th></th>
                <th onClick={() => handleSort('documentNumber')} className={`sortable ${sortConfig.column === 'documentNumber' ? 'sorted' : ''}`}>
                  Document # {sortConfig.column === 'documentNumber' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('customer')} className={`sortable ${sortConfig.column === 'customer' ? 'sorted' : ''}`}>
                  Customer {sortConfig.column === 'customer' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('createdBy')} className={`sortable ${sortConfig.column === 'createdBy' ? 'sorted' : ''}`}>
                  Created By {sortConfig.column === 'createdBy' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('createdAt')} className={`sortable ${sortConfig.column === 'createdAt' ? 'sorted' : ''}`}>
                  Date {sortConfig.column === 'createdAt' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('total')} className={`sortable ${sortConfig.column === 'total' ? 'sorted' : ''}`}>
                  Total {sortConfig.column === 'total' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('status')} className={`sortable ${sortConfig.column === 'status' ? 'sorted' : ''}`}>
                  Status {sortConfig.column === 'status' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedDocuments.map(doc => {
                const date = doc.createdAt ? new Date(doc.createdAt) : null;
                return (
                  <tr key={doc._id} className={selectedRows.includes(doc._id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(doc._id)}
                        onChange={() => handleCheckboxChange(doc._id)}
                      />
                    </td>
                    <td>{doc.documentNumber}</td>
                    <td>{doc.customer}</td>
                    <td>{doc.createdBy || 'Unknown'}</td>
                    <td>{date ? date.toLocaleDateString() : ''}</td>
                    <td>
                      <div>{formatCurrency(doc.total)}</div>
                      {doc.computedtax !== undefined && doc.computedtax > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Tax: {formatCurrency(doc.computedtax)}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusLight
                        status={doc.status || 'Draft'}
                        onClick={() => handleStatusClick(doc._id, doc.status || 'Draft')}
                        isClickable={permissions.canPayments && (doc.status === 'Confirmed' || doc.status === 'In Progress')}
                        loading={statusUpdatingId === doc._id}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>
      
      <DocumentModal 
        invoice={selectedDocument}
        isOpen={modalOpen}
        onClose={closeModal}
        currentLogo={currentLogo}
        onPaymentStatusChanged={fetchInvoices}
      />
      
      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentDocumentId(null);
        }}
        paymentType="invoice"
        entity={paymentDocumentId}
        onPaymentSaved={handlePaymentSaved}
      />

      {/* Copy Confirmation Modal */}
      {showCopyConfirm && documentToCopy && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={cancelCopyDocument}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>Copy Invoice</h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Create a copy of <strong>{documentToCopy.documentNumber}</strong>?
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button onClick={cancelCopyDocument} style={{
                padding: '8px 16px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}>
                Cancel
              </button>
              <button onClick={confirmCopyDocument} disabled={copyLoading} style={{
                padding: '8px 16px',
                backgroundColor: '#8b5cf6',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: copyLoading ? 'not-allowed' : 'pointer',
                color: 'white'
              }}>
                {copyLoading ? 'Copying...' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowFilterModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Filter Invoices
            </h2>

            {/* Status Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Status
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {['Draft', 'Confirmed', 'In Progress', 'Completed', 'Cancelled'].map(status => (
                  <button
                    key={status}
                    onClick={() => handleFilterChange(selectedFilters.status === status ? null : status)}
                    style={{
                      padding: '10px',
                      backgroundColor: selectedFilters.status === status ? '#0ea5e9' : '#f3f4f6',
                      border: selectedFilters.status === status ? '2px solid #0284c7' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      color: selectedFilters.status === status ? 'white' : '#374151',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937'
                }}
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#0ea5e9',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && documentToCancel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowCancelConfirm(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Cancel Invoice
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to cancel <strong>{documentToCancel.documentNumber}</strong>? This will mark the invoice as cancelled.
            </p>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Cancellation Reason
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px',
                resize: 'vertical'
              }}
              placeholder="Enter reason for cancellation"
            />
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowCancelConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937'
                }}
              >
                No, Keep It
              </button>
              <button
                onClick={confirmCancelDocument}
                disabled={!cancelReason.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: cancelReason.trim() ? 'pointer' : 'not-allowed',
                  color: 'white'
                }}
              >
                Yes, Cancel Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && documentToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Delete Invoice
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to delete <strong>{documentToDelete.documentNumber}</strong>? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDocument}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <DocumentCreationModal
        isOpen={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        documentType="Invoice"
        onDocumentSaved={handleDocumentCreated}
      />

      <DocumentCreationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setDocumentToEdit(null);
        }}
        documentType="Invoice"
        initialDocument={documentToEdit}
        onDocumentSaved={handleDocumentCreated}
      />
    </div>
  );
};

export default InvoiceListPage;
